import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import Button from '@mui/material/Button';
import { PillButton, ControlledTextField } from '@zz2/zz2-ui';
import { ControlledLabelledCheckBox } from '../../../../components/input/form/ControlledLabelledCheckBoxWrapper';
import CustomTooltip from '../../../../components/tooltip/tooltip';
import { yupResolver } from '@hookform/resolvers/yup';
import DepartmentModelHelper from '../../../../@types/model/masterData/department/departmentModelHelper';
import { IDepartmentFormValues } from '../../../../@types/model/masterData/department/departmentFormValues';
import CustomReactHookFormDevTools from '../../../../components/devtools/ReactHookFormDevTools';

interface IDepartmentFormProps {
    isLoading : boolean;
    initialValues : IDepartmentFormValues;
    onSubmit : (values : IDepartmentFormValues) => void;
    onClose : () => void;
    isImported : boolean;
}

const DepartmentForm = (props : IDepartmentFormProps) : React.ReactElement => {

    const { handleSubmit, control, formState: { isValid, isDirty }, trigger } = useForm<IDepartmentFormValues>({
        defaultValues: props.initialValues,
        resolver: yupResolver(DepartmentModelHelper.formSchema()),
        mode: 'onChange',
    });

    const tooltipTitle = props.isImported ? 'Cannot change this field if department is imported' : '';

    /*================================================================================================================
     *                                                  Queries
     * ==============================================================================================================*/

    /*================================================================================================================
     *                                                  Effects
     * ==============================================================================================================*/

    useEffect(() => {
        trigger();
    }, []);

    /*================================================================================================================
     *                                                  Async Methods
     * ==============================================================================================================*/

    /*================================================================================================================
     *                                                  Handler Methods
     * ==============================================================================================================*/

    /*================================================================================================================
     *                                                  Memos
     * ==============================================================================================================*/

    /*================================================================================================================
     *                                                  Render Methods
     * ==============================================================================================================*/

    return (
        <div>
            <form onSubmit={handleSubmit(props.onSubmit)} className={'p20'}>
                <div className={'fdc'}>
                    <CustomTooltip title={tooltipTitle}>
                        <ControlledTextField
                            className={'mb10 w500'}
                            name={'code'}
                            label={'Code'}
                            control={control}
                            defaultValue={props.initialValues.code}
                            disabled={props.isImported}
                            size={'small'}
                        />
                    </CustomTooltip>
                    <CustomTooltip title={tooltipTitle}>
                        <ControlledTextField
                            className={'mb10 w500'}
                            name={'shortDescription'}
                            label={'Short Description'}
                            control={control}
                            defaultValue={props.initialValues.shortDescription}
                            disabled={props.isImported}
                            size={'small'}
                        />
                    </CustomTooltip>
                    <CustomTooltip title={tooltipTitle}>
                        <ControlledTextField
                            className={'mb10 w500'}
                            name={'description'}
                            label={'Description'}
                            control={control}
                            defaultValue={props.initialValues.description}
                            disabled={props.isImported}
                            size={'small'}
                        />
                    </CustomTooltip>
                    <CustomTooltip title={tooltipTitle}>
                        <div className={'fdr jcfs mb5'}>
                            <ControlledLabelledCheckBox
                                className={'p0'}
                                name={'isActive'}
                                label={'Is Active'}
                                control={control}
                                defaultValue={props.initialValues.isActive}
                                disabled={props.isImported || props.initialValues.isActive}
                            />
                        </div>
                    </CustomTooltip>
                    <div className={'fdr flx1 aic jcfe'}>
                        <Button 
                            className={'cpd mr20'}
                            onClick={props.onClose} 
                            type={'reset'}>
                            CANCEL
                        </Button>
                        <PillButton
                            className={'pl30 pr30'}
                            text={'SAVE'}
                            size={'small'}
                            type={'submit'}
                            color={'secondary'}
                            disabled={ !isDirty || !isValid || props.isLoading }
                        />
                    </div>
                </div>
            </form>
            <CustomReactHookFormDevTools control={control} />
        </div>
    );
};

export default DepartmentForm;
